import { createSlice } from '@reduxjs/toolkit';

import axios from 'utils/axiosClient';
import { dispatch } from '../index';

// Giới hạn phải khớp KeHoachThucTapFile bên backend. Chặn ở đây chỉ để báo lỗi ngay cho hạnh
// sinh, không phải hàng rào thật — backend vẫn kiểm tra lại dung lượng thật trên S3.
export const MAX_BYTES = 50 * 1024 * 1024;

// Mạng yếu: thà báo lỗi rõ ràng còn hơn để vòng xoay quay mãi không biết bao giờ dừng.
const TIMEOUT_GOI_API = 20000;
const TIMEOUT_TAI_TEP = 30 * 60 * 1000;
export const DUOI_CHO_PHEP = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];

// TẠM ẨN (23/09/2026): ban quản lý chưa cần tên đề tài. Ô nhập biến mất và không còn bắt
// buộc; backend cũng đã nhận đề tài rỗng. Mở lại: đổi thành false, không cần đụng gì khác.
export const TAM_AN_TEN_DE_TAI = true;

const initialState = {
    maHs: '',
    hoTen: '',
    nhom: '',
    donVi: '',
    // Phiếu đã nộp của chính hạnh sinh; null = chưa nộp lần nào.
    phieu: null,
    loaded: false,
    saving: false,
    error: null,
    // Danh sách cho ban quản lý.
    danhSach: [],
    danhSachLoaded: false
};

const slice = createSlice({
    name: 'keHoachThucTap',
    initialState,
    reducers: {
        // Store là module-level và không persist: đổi tài khoản trong cùng tab thì state của
        // người trước còn nguyên trong RAM. Đặt lại TOÀN BỘ trước mỗi lần tải.
        datLai() {
            return { ...initialState };
        },
        hasError(state, action) {
            state.error = action.payload;
            state.loaded = true;
            state.danhSachLoaded = true;
            // Xoá dữ liệu lần tải trước: request hỏng thì không được để hạnh sinh mới đăng nhập
            // nhìn thấy phiếu của người trước.
            state.phieu = null;
            state.danhSach = [];
        },
        getHienTaiSuccess(state, action) {
            state.maHs = action.payload?.maHs ?? '';
            state.hoTen = action.payload?.hoTen ?? '';
            state.nhom = action.payload?.nhom ?? '';
            state.donVi = action.payload?.donVi ?? '';
            state.phieu = action.payload?.phieu ?? null;
            state.loaded = true;
            state.error = null;
        },
        saveStart(state) {
            state.saving = true;
            state.error = null;
        },
        saveSuccess(state, action) {
            state.phieu = action.payload;
            state.saving = false;
        },
        // Lỗi GỬI cố ý không ghi vào `error`: `error` chỉ dành cho lỗi TẢI, trang lật sang
        // màn hình "không tải được" theo nó. Một lần gửi hỏng chỉ hiện Alert, không xoá form.
        saveFailure(state) {
            state.saving = false;
        },
        // Hỏi lại phiếu trong lúc chờ Lambda chuyển Word → PDF. Chỉ thay phiếu khi vẫn là cùng tệp:
        // phản hồi về muộn không được đè lên phiếu mới hơn vừa nộp.
        capNhatPhieu(state, action) {
            const moi = action.payload?.phieu;
            if (moi && (!state.phieu || state.phieu.fileKey === moi.fileKey)) state.phieu = moi;
        },
        getDanhSachSuccess(state, action) {
            state.danhSach = action.payload ?? [];
            state.danhSachLoaded = true;
            state.error = null;
        }
    }
});

export default slice.reducer;

export const { datLai } = slice.actions;

/**
 * Lời nhắn lỗi đọc được cho hạnh sinh.
 *
 * Thứ tự có chủ ý: câu của backend là cụ thể nhất nên ưu tiên; sau đó tới hai tình huống mạng
 * yếu hay gặp, vì axios ném ra "timeout of 20000ms exceeded" / "Network Error" — hạnh sinh
 * đọc vào không hiểu gì. Cuối cùng mới tới lỗi do chính mình ném (đã viết sẵn tiếng Việt).
 */
function loiMang(error, macDinh) {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.code === 'ECONNABORTED') return 'Mạng chậm nên yêu cầu quá hạn. Kiểm tra sóng rồi thử lại.';
    if (error?.code === 'ERR_NETWORK') return 'Mất kết nối. Kiểm tra mạng rồi thử lại.';
    if (error?.response) return macDinh;

    return error?.message || macDinh;
}

/** Kiểm tra tệp ngay khi hạnh sinh chọn; trả về lời nhắn lỗi hoặc null. */
export function loiTep(file) {
    if (!file) return 'Vui lòng chọn tệp kế hoạch thực tập.';

    const cham = file.name.lastIndexOf('.');
    const duoi = cham < 0 ? '' : file.name.slice(cham).toLowerCase();

    if (!DUOI_CHO_PHEP.includes(duoi)) {
        return 'Chỉ nhận tệp Word, PDF hoặc ảnh (.pdf, .doc, .docx, .jpg, .jpeg, .png).';
    }

    if (file.size <= 0) return 'Tệp rỗng, vui lòng chọn lại.';
    if (file.size > MAX_BYTES) return 'Tệp vượt quá 50MB, vui lòng chọn tệp nhỏ hơn.';

    return null;
}

const DUOI_ANH = ['.jpg', '.jpeg', '.png'];
// Bài viết tay chụp thành nhiều ảnh: cho ghép tối đa ngần này trang vào một PDF.
export const TOI_DA_ANH = 10;

export const laAnh = (file) => {
    const cham = (file?.name || '').lastIndexOf('.');

    return cham >= 0 && DUOI_ANH.includes(file.name.slice(cham).toLowerCase());
};

/**
 * Gộp các tệp vừa chọn vào danh sách đang có.
 *
 * - Ảnh: được chọn nhiều và cộng dồn (chụp bài thành 2, 3 ảnh), khi gửi sẽ ghép thành 1 PDF.
 * - Word/PDF: chỉ một tệp, thay thế mọi thứ đang chọn — không trộn chung với ảnh.
 *
 * Trả về { teps, loi }: `teps` luôn là danh sách hợp lệ nên giữ lại, `loi` (nếu có) để báo.
 */
export function gopTepChon(hienTai, moi) {
    const ds = Array.from(moi || []);
    if (!ds.length) return { teps: hienTai, loi: null };

    const khongPhaiAnh = ds.filter((f) => !laAnh(f));
    if (khongPhaiAnh.length) {
        if (ds.length > 1) return { teps: hienTai, loi: 'Tệp Word hoặc PDF chỉ chọn một tệp. Ảnh thì chọn được nhiều ảnh.' };

        const loi = loiTep(ds[0]);

        return loi ? { teps: hienTai, loi } : { teps: [ds[0]], loi: null };
    }

    const coSan = hienTai.every(laAnh) ? hienTai : [];
    const loiAnh = ds.map(loiTep).find(Boolean);
    const hopLe = ds.filter((f) => !loiTep(f));
    const gop = [...coSan, ...hopLe];

    if (gop.length > TOI_DA_ANH) {
        return { teps: gop.slice(0, TOI_DA_ANH), loi: `Tối đa ${TOI_DA_ANH} ảnh cho một kế hoạch — đã giữ ${TOI_DA_ANH} ảnh đầu.` };
    }

    return { teps: gop, loi: loiAnh || null };
}

export function getKeHoachThucTapHienTai() {
    return async () => {
        dispatch(slice.actions.datLai());

        try {
            const response = await axios.get('/kehoachthuctap/current', { timeout: TIMEOUT_GOI_API });
            dispatch(slice.actions.getHienTaiSuccess(response));
        } catch (error) {
            dispatch(slice.actions.hasError(loiMang(error, 'Không tải được thông tin kế hoạch thực tập.')));
        }
    };
}

/** Tải lại phiếu hiện tại mà KHÔNG đặt lại trang (không nháy "Đang tải…"). Lỗi thì bỏ qua. */
export function lamMoiPhieu() {
    return async () => {
        try {
            const response = await axios.get('/kehoachthuctap/current', { timeout: TIMEOUT_GOI_API });
            dispatch(slice.actions.capNhatPhieu(response));
        } catch (error) {
            // Chỉ là lần hỏi lại nền; lần sau hỏi tiếp.
        }
    };
}

/**
 * Đẩy tệp thẳng lên S3 bằng presigned URL do backend cấp. Trả về object key để bước lưu phiếu
 * gửi kèm — key do backend dựng, frontend chỉ chuyển tiếp nguyên văn.
 */
export function uploadTep(file, onTienDo) {
    return async () => {
        const loi = loiTep(file);
        if (loi) return { ok: false, message: loi };

        // Content-Type nằm trong chữ ký của presigned URL: chuỗi gửi lúc xin URL và chuỗi gửi
        // lúc PUT phải giống hệt nhau, lệch một ký tự là S3 trả 403 SignatureDoesNotMatch.
        // Tính một lần ở đây rồi dùng cho cả hai, backend chuẩn hoá rỗng y hệt.
        const contentType = file.type || 'application/octet-stream';

        try {
            const preSign = await axios.post(
                '/kehoachthuctap/signupload',
                { fileName: file.name, contentType, fileSize: file.size },
                { timeout: TIMEOUT_GOI_API }
            );

            await dayLenS3(preSign.url, file, contentType, onTienDo);

            return { ok: true, key: preSign.key };
        } catch (error) {
            return { ok: false, message: loiMang(error, 'Tải tệp lên không thành công, vui lòng thử lại.') };
        }
    };
}

/**
 * Đẩy tệp lên S3 bằng XMLHttpRequest chứ không phải fetch.
 *
 * fetch KHÔNG báo được tiến độ tải lên. Tệp tới 50MB trên mạng yếu có thể mất nhiều phút, mà
 * màn hình chỉ đứng im chữ "Đang tải tệp…" thì hạnh sinh tưởng treo và bấm lại hoặc thoát ra.
 * XHR có `upload.onprogress` nên vẽ được phần trăm thật.
 */
function dayLenS3(url, file, contentType, onTienDo) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', contentType);

        // KHÔNG gửi thêm header nào khác (nhất là Access-Control-Allow-Origin): mọi header
        // ngoài dự tính đều làm lệch chữ ký hoặc vô ích — CORS là việc của cấu hình bucket.

        // 30 phút: đủ cho 50MB ở đường truyền rất chậm, nhưng vẫn kết thúc thay vì treo mãi.
        xhr.timeout = TIMEOUT_TAI_TEP;

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onTienDo) onTienDo(Math.round((e.loaded / e.total) * 100));
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error('Tải tệp lên không thành công, vui lòng thử lại.'));
        };

        xhr.onerror = () => reject(new Error('Mất kết nối khi đang tải tệp. Kiểm tra mạng rồi gửi lại.'));
        xhr.ontimeout = () => reject(new Error('Tải tệp quá lâu, mạng có vẻ yếu. Thử lại khi sóng tốt hơn.'));
        xhr.onabort = () => reject(new Error('Đã huỷ tải tệp.'));

        xhr.send(file);
    });
}

export function saveKeHoachThucTap({ tenDeTai, fileKey }) {
    return async () => {
        dispatch(slice.actions.saveStart());

        try {
            const response = await axios.post('/kehoachthuctap', { tenDeTai, fileKey }, { timeout: TIMEOUT_GOI_API });
            dispatch(slice.actions.saveSuccess(response));
            return { ok: true };
        } catch (error) {
            const message = loiMang(error, 'Không gửi được kế hoạch, vui lòng thử lại.');
            dispatch(slice.actions.saveFailure());
            return { ok: false, message };
        }
    };
}

export function getDanhSachKeHoachThucTap() {
    return async () => {
        try {
            const response = await axios.get('/kehoachthuctap/bql', { timeout: TIMEOUT_GOI_API });
            dispatch(slice.actions.getDanhSachSuccess(response?.results));
        } catch (error) {
            dispatch(slice.actions.hasError(loiMang(error, 'Không tải được danh sách kế hoạch thực tập.')));
        }
    };
}
